<template>
    <td
        :rowspan="rowSpan"
        :class="type + (useLineTextEllipsis ? ' ellipsis' : '') + (cssClass !== '' ? ' ' + cssClass : '') + (removedValue !== '' ? ' dependency-warning' : '')"
        :title="removedValue !== '' ? 'removed value(s) \'' + removedValue + '\' due dependency' : ''"
    >
        <v-autocomplete
            v-if="visible"
            v-model="selectValue"
            :items="allValues"
            item-value="id"
            item-text="name"
            item-color="var(--sc-blue-dark)"
            :multiple="multiSelect"
            :menu-props="{maxHeight: 800}"
            @change="changeSelection()"
            :hide-no-data="true"
            :loading="loading ? 'loading' : false"
            :disabled="loading"
            :readonly="readonly"
        >
            <template v-if="multiSelect" v-slot:prepend-item>
                <v-list-item
                    @click="selectAllToggle"
                >
                    <v-list-item-action>
                        <v-icon color="var(--sc-blue-dark)">{{ icon }}</v-icon>
                    </v-list-item-action>
                    <v-list-item-content>
                        <v-list-item-title><strong>Select All</strong></v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
                <v-divider
                    slot="prepend-item"
                    class="mt-2"
                />
            </template>
            <!--
            Template for showing initial please select and all values
            -->
            <template v-slot:prepend-inner>
                <span v-if="multiSelect && selectValue === null" class="text-no-wrap">-- all values --</span>
                <span v-else-if="selectValue === ''" class="text-no-wrap">{{$t(emptyText)}}</span>
            </template>
        </v-autocomplete>
        <div v-if="!readonly && showAddRow && visible" class="addRowPlus" :type="type" @click="addRow"/>
    </td>
</template>

<script>
    import i18n from "../../i18n";

    export default {
        name: "TagsSelectTableCell",
        props: {
            type:            String,
            allValuesObject: Object,
            allValuesArray:  [Array],
            value: {
                type: String,
                validator: function(value) {
                    return (typeof value === 'string') || value === null;
                }
            },

            codeTexts: {
                type:    [Boolean,String],
                default: false
            },
            cssClass: {
                type: String,
                default: ''
            },
            multiSelect: {
                type:    Boolean,
                default: false
            },
            rowSpan: {
                type:    Number,
                default: 1
            },
            useLineTextEllipsis: {
                type:    Boolean,
                default: false
            },
            showAllValuesAsValues: {
                type:    Boolean,
                default: false
            },
            showAddRow: {
                type:    Boolean,
                default: false
            },
            addEmptyOption: {
                type:    Boolean,
                default: false
            },
            emptyOptionText: {
                type:    String,
                default: '-- unset --'
            },
            emptyText: {
                type:    String,
                default: '-- please select --'
            },
            displayShortAllValues: {
                type:    Boolean,
                default: false
            },
            readonly: {
                type:    Boolean,
                default: false
            },
            visible: {
                type:    Boolean,
                default: true
            }
        },
        data() {
            return {
                currentValue:        this.value,
                lineBreak:           '\n',
                specialLineBreaks: {
                    inspectionCriteria: ', '
                },
                onHide:              false,
                allValuesArrayLocal: this.allValuesArray,
                loading:             false,
                removedValue:        ''
            }
        },
        mounted() {
            /* deactivated dependencies
            if (
                this.value !== ''      // skip empty values
                && this.value !== null // and all values
            ) {
                this.checkValueAvailabilityAndPopulateRemovedValue(this.value.split(','));
            }
            */
        },
        computed: {
            valueCount() {
                return Object.keys(this.allValuesObject).length;
            },
            selectValue: {
                // getter
                get: function () {
                    if (this.multiSelect) {
                        if (this.currentValue === null) {
                            // null => all values
                            if (this.displayShortAllValues) {
                                return null;
                            } else {
                                return Object.keys(this.allValuesObject);
                            }
                        } else if (this.currentValue === '') {
                            return '';
                        } else {
                            let values = this.currentValue.split(',');
                            if (
                                this.allValuesObject === null
                                || values.length === Object.keys(this.allValuesObject).length
                            ) {
                                return '';
                            }
                            // check removed values
                            if (this.removedValue !== '') {
                                // remove removed value from selection
                                this.removedValue.split(',').forEach((removedValue) => {
                                    if (values.includes(removedValue)) {
                                        values.splice(values.indexOf(removedValue), 1);
                                    }
                                });
                                if (values.length === 0) {
                                    return '';
                                }
                            }
                            return values;
                        }
                    } else {
                        if (this.currentValue === this.removedValue) {
                            return '';
                        } else {
                            return this.currentValue;
                        }
                    }
                },
                // setter
                set: function (newValue) {
                    if (this.multiSelect) {
                        if (
                            newValue === null
                            || newValue.length === this.valueCount
                        ) {
                            // null => all values
                            this.currentValue = null
                        } else if (newValue === '') {
                            this.currentValue = '';
                        } else {
                            newValue = newValue.filter(function (el) {return el !== '';});
                            this.currentValue = newValue.join(',');
                            //this.checkValueAvailabilityAndPopulateRemovedValue(newValue);
                        }
                    } else {
                        this.currentValue = newValue;
                        //this.checkValueAvailabilityAndPopulateRemovedValue(newValue);
                    }
                }
            },
            icon() {
                if (this.currentValue === '') {
                    // none selected
                    return 'mdi-checkbox-blank-outline'; // empty checkbox
                } else if (this.currentValue === null) {
                    // all selected
                    return 'mdi-checkbox-marked'; // checked checkbox
                } else {
                    // some selected
                    return 'mdi-minus-box'; // checkbox with minus
                }
            },
            /* Returns the cell content depending on column type, showAllValuesAsValues, useLineTextEllipsis and special line break  */
            allValues() {
                // console.log('TagsSelectTableCell, all Values', this.allValuesArrayLocal, this.type)
                let allValues = [];

                if (this.addEmptyOption) {
                    allValues.push({
                        id:       null,
                        name:     i18n.t(this.emptyOptionText),
                    });
                }

                for (let key in this.allValuesArrayLocal) {
                    allValues.push({
                        id: this.allValuesArrayLocal[key].id,
                        name: this.codeTexts ? this.allValuesArrayLocal[key].name : this.allValuesArrayLocal[key].id
                    });
                }

                // if (this.type === 'componentCode')
                // console.log('all values', allValues)
                return allValues;
            },
            allValueKeysAsArray()
            {
                return this.allValues.map(item => item.id);
            }
        },
        methods: {
            addRow() {
                if (this.readonly) {
                    return;
                }
                this.$emit('addRow');
            },
            changeSelection() {
                if (this.readonly) {
                    return;
                }

                // emit change selection event
                // multi selects should only fire when close the list with the blur event to reduce render effort
                // but there is a bug if the multiselect value is empty,
                // the blur fires after first selection and no on list close :(
                this.$emit('changeSelection', this.selectValue);
            },
            selectAllToggle() {
                if (this.readonly) {
                    return;
                }
                // direction: some -> all -> none -> all -> none ...
                if (this.currentValue !== null) {
                    this.currentValue = null;
                } else {
                    this.currentValue = '';
                }

                this.$emit('changeSelection', this.selectValue);
            },
            /* Returns the text of value depending on column type, codeTexts parameter and existence of allValuesObject */
            getElementText(value) {
                let text;
                if (this.allValuesObject) {
                    if (this.codeTexts === true) {
                        text = this.allValuesObject[value];
                        if (
                            this.type !== 'serviceType'
                            && this.type !== 'surveyType'
                        ) {
                            text = value + ' - ' + text;
                        }
                    } else if (this.codeTexts === 'label') {
                        text = this.allValuesObject[value];
                    } else {
                        if (
                            this.type !== 'serviceType'
                            && this.type !== 'surveyType'
                        ) {
                            text = value;
                        } else {
                            text = this.allValuesObject[value];
                        }
                    }
                } else {
                    text = value;
                }

                return text;
            },
            updateSelectableEntries(data)
            {
                if (this.readonly) {
                    return [];
                }

                let oldCurrentValue = this.currentValue;

                // if current value is not selectable anymore
                // and some values are selected (not all and not none)
                // unset current value
                if (
                    this.selectValue !== null
                    && this.selectValue !== ''
                ) {
                    if (this.multiSelect) {
                        let newValue = this.selectValue;
                        this.selectValue.forEach((value) => {
                            if (!data.includes(value)) {
                                newValue.splice(this.selectValue.indexOf(value), 1);
                            }
                        });
                        this.selectValue = newValue;
                    } else if (!data.includes(this.selectValue)) {
                        this.selectValue = '';
                    }
                }

                // convert selectable entries to value object array
                let selectableObjectArray = [];
                let selectableKeyArray    = [];
                data.forEach(value => {
                    selectableObjectArray.push({
                        id: value,
                        name: this.getElementText(value)
                    });
                    selectableKeyArray.push(value);
                });

                this.checkValueAvailabilityAndPopulateRemovedValue(oldCurrentValue);

                this.allValuesArrayLocal = selectableObjectArray;
            },
            setLoading(state)
            {
                this.loading = !!state;
            },
            checkValueAvailabilityAndPopulateRemovedValue(values)
            {
                if (
                    values === null
                    || values === ''
                ) {
                    this.removedValue = '';
                } else {
                    if (typeof values === 'string') {
                        values = values.split(',');
                    }
                    let removedValues = [];
                    values.forEach(key => {
                        if (!this.allValueKeysAsArray.includes(key)) {
                            removedValues.push(key);
                        }
                    });
                    this.removedValue = removedValues.join(',');
                }
            }
        }
    }
</script>

<style scoped>
    div.editor-box table.vcs-table td.ellipsis {
        white-space: pre-line;
    }
    div.editor-box table.vcs-table td.ellipsis div.ellipsis {
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
        white-space: nowrap;
    }
</style>